
import { memo } from 'react';
import { SessionCard } from './SessionCard';
import { ShowAllButton } from './sessions-list/ShowAllButton';
import { EmptyState } from './sessions-list/EmptyState';
import { SessionDetailsDialog } from './SessionDetailsDialog';
import { SessionEditForm } from './sessions-list/SessionEditForm';
import { useSessionsList } from '../components/sessions-list/useSessionsList';
import { TrainingSession } from '../types';

interface SessionsListProps {
  title?: string;
  sessions: TrainingSession[] | null;
  maxDisplay?: number;
  showAllUrl?: string;
  emptyMessage?: string;
  searchQuery?: string;
}

export const SessionsList = memo(function SessionsList({
  sessions,
  maxDisplay = 6,
  searchQuery = ''
}: SessionsListProps) {
  // Use the useSessionsList hook to handle filtering and other operations
  const {
    displayedSessions,
    filteredSessions,
    selectedSession,
    isDetailsOpen,
    setIsDetailsOpen,
    sessionToEdit,
    isEditFormOpen,
    setIsEditFormOpen,
    handleViewDetails,
    handleEdit,
    handleShowAll,
    handleShowLess,
    showingAll,
    deleteSession,
    isSearching
  } = useSessionsList({
    sessions,
    searchQuery,
    limit: maxDisplay,
    showAllButton: !!maxDisplay
  });

  // Handle delete operation
  const handleDelete = async (session: TrainingSession) => {
    if (deleteSession) {
      await deleteSession(session.id);
    }
  };

  // Check if sessions are empty after filtering by search query
  if (filteredSessions.length === 0) {
    return <EmptyState searchQuery={searchQuery} />;
  }

  return (
    <div className="space-y-4">
      {/* Stacked layout for sessions */}
      <div className="flex flex-col space-y-4">
        {displayedSessions.map((session: TrainingSession) => (
          <SessionCard 
            key={session.id} 
            session={session} 
            onViewDetails={handleViewDetails} 
            onEdit={handleEdit} 
            onDelete={handleDelete} 
          />
        ))}
      </div>
      
      {/* Only show the "Show All" button when not searching and there are more sessions than displayed */}
      {!isSearching && filteredSessions.length > maxDisplay && (
        <ShowAllButton 
          totalCount={filteredSessions.length} 
          displayedCount={displayedSessions.length} 
          showingAll={showingAll}
          onShowAll={handleShowAll} 
          onShowLess={handleShowLess}
        />
      )}
      
      {selectedSession && (
        <>
          <SessionDetailsDialog 
            session={selectedSession} 
            open={isDetailsOpen} 
            onOpenChange={setIsDetailsOpen} 
            onEdit={handleEdit} 
          />
        </>
      )}
      
      <SessionEditForm 
        session={sessionToEdit} 
        isOpen={isEditFormOpen} 
        onOpenChange={setIsEditFormOpen} 
        onSuccess={() => {
          setIsEditFormOpen(false);
        }} 
      />
    </div>
  );
});
